import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Permission, SessionUser } from "@ir/types";
import {
  getSession,
  setUnauthenticatedHandler,
  signOut as signOutRequest,
  startGoogleLogin,
} from "@ir/services";

/**
 * Session state for the admin surface.
 *
 * This context decides what to *render*. It is not a security boundary — every
 * `/api/admin/*` call is authorised again server-side against the user's stored
 * role, so hiding a button here and enforcing it there are two separate jobs.
 *
 * The session is re-checked on an interval and when the tab regains focus, so a
 * user who is disabled mid-shift is bounced within a minute rather than at
 * cookie expiry.
 */

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  status: AuthStatus;
  user: SessionUser | null;
  error: string | null;
  can: (permission: Permission) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  signIn: (options?: { returnTo?: string; invite?: string | null }) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REVALIDATE_INTERVAL_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const session = await getSession();
      if (!mounted.current) return;
      setUser(session.user);
      setStatus(session.authenticated && session.user ? "authenticated" : "unauthenticated");
      setError(null);
    } catch (caught) {
      if (!mounted.current) return;
      setUser(null);
      setStatus("unauthenticated");
      setError(
        caught instanceof Error
          ? caught.message
          : "We could not check your sign-in status.",
      );
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  // Any 401 from an admin call means the session died; drop straight to signed
  // out rather than leaving half-broken panels on screen.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setUser(null);
      setStatus("unauthenticated");
    });
    return () => setUnauthenticatedHandler(null);
  }, []);

  useEffect(() => {
    if (status !== "authenticated") return;

    const interval = setInterval(() => void load(), REVALIDATE_INTERVAL_MS);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [status, load]);

  const permissions = useMemo(
    () => new Set<Permission>(user?.permissions ?? []),
    [user],
  );

  const can = useCallback(
    (permission: Permission) => permissions.has(permission),
    [permissions],
  );

  const canAny = useCallback(
    (required: Permission[]) => required.some((permission) => permissions.has(permission)),
    [permissions],
  );

  const signOut = useCallback(async () => {
    try {
      await signOutRequest();
    } finally {
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      error,
      can,
      canAny,
      signIn: startGoogleLogin,
      signOut,
      refresh: load,
    }),
    [status, user, error, can, canAny, signOut, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an <AuthProvider>");
  }
  return context;
}
